// ============================================================================
// ROLE SERVICE
// ============================================================================
// Business logic and API calls for the Role Master module.
// Covers every endpoint exposed by RoleController:
//   CRUD, lookups by code/type/application, Keycloak sync, and scope assignments.

import { get, post, put, del } from '../client/axiosClient';
import { roleEndpoints } from '../endpoints/roles.endpoints';
import type {
  ID,
  RoleResponse,
  CreateRoleRequest,
  UpdateRoleRequest,
  KeycloakRoleResponse,
  RoleExistenceValidation,
  RoleScopeAssignmentResponse,
  ScopeDefaultsSummary,
  UpdateRoleScopeDefaultRequest,
} from '../types';
import type { TenantHubApiResponse } from '../types/auth.types';

/**
 * Role Service Class
 */
class RoleService {
  // ── CRUD ────────────────────────────────────────────────────────────────

  /**
   * Get all roles.
   * GET /Role
   */
  async getAll(): Promise<TenantHubApiResponse<RoleResponse[]>> {
    const response = await get<TenantHubApiResponse<RoleResponse[]>>(roleEndpoints.list());
    return response.data;
  }

  /**
   * Get a role by ID.
   * GET /Role/:id
   */
  async getById(id: ID): Promise<TenantHubApiResponse<RoleResponse>> {
    const response = await get<TenantHubApiResponse<RoleResponse>>(roleEndpoints.getById(id));
    return response.data;
  }

  /**
   * Get a role by its unique code. Used by the form to detect duplicates
   * before submitting a create.
   * GET /Role/code/:code
   */
  async getByCode(code: string): Promise<TenantHubApiResponse<RoleResponse>> {
    const response = await get<TenantHubApiResponse<RoleResponse>>(roleEndpoints.getByCode(code));
    return response.data;
  }

  /**
   * Get roles of a given type (server-side filter).
   * GET /Role/type/:roleType
   */
  async getByType(roleType: string): Promise<TenantHubApiResponse<RoleResponse[]>> {
    const response = await get<TenantHubApiResponse<RoleResponse[]>>(roleEndpoints.getByType(roleType));
    return response.data;
  }

  /**
   * Get roles belonging to an application (server-side filter).
   * GET /Role/app/:appId
   */
  async getByAppId(appId: ID): Promise<TenantHubApiResponse<RoleResponse[]>> {
    const response = await get<TenantHubApiResponse<RoleResponse[]>>(roleEndpoints.getByAppId(appId));
    return response.data;
  }

  /**
   * Create a role (optionally mirrored into Keycloak).
   * POST /Role
   */
  async create(data: CreateRoleRequest): Promise<TenantHubApiResponse<RoleResponse>> {
    const response = await post<TenantHubApiResponse<RoleResponse>>(roleEndpoints.create(), data);
    return response.data;
  }

  /**
   * Update a role. Code, type and application are immutable server-side.
   * PUT /Role/:id
   */
  async update(id: ID, data: UpdateRoleRequest): Promise<TenantHubApiResponse<RoleResponse>> {
    const response = await put<TenantHubApiResponse<RoleResponse>>(roleEndpoints.update(id), data);
    return response.data;
  }

  /**
   * Delete a role. NOTE: this is a hard delete on the backend — the API
   * exposes no deactivate/soft-delete for roles.
   * DELETE /Role/:id
   */
  async delete(id: ID): Promise<TenantHubApiResponse<boolean>> {
    const response = await del<TenantHubApiResponse<boolean>>(roleEndpoints.delete(id));
    return response.data;
  }

  // ── Keycloak synchronisation ────────────────────────────────────────────

  /**
   * Push one role to Keycloak as a CLIENT role.
   * POST /Role/:id/sync-to-keycloak
   */
  async syncToKeycloak(id: ID, realmName: string, clientId: string): Promise<TenantHubApiResponse<RoleResponse>> {
    const response = await post<TenantHubApiResponse<RoleResponse>>(
      roleEndpoints.syncToKeycloak(id, realmName, clientId),
      {},
    );
    return response.data;
  }

  /**
   * Push every role to Keycloak as CLIENT roles.
   * POST /Role/sync-all-to-keycloak
   */
  async syncAllToKeycloak(realmName: string, clientId: string): Promise<TenantHubApiResponse<RoleResponse[]>> {
    const response = await post<TenantHubApiResponse<RoleResponse[]>>(
      roleEndpoints.syncAllToKeycloak(realmName, clientId),
      {},
    );
    return response.data;
  }

  /**
   * List the CLIENT roles Keycloak currently holds.
   * GET /Role/keycloak/:realmName
   */
  async getKeycloakRoles(realmName: string, clientId: string): Promise<TenantHubApiResponse<KeycloakRoleResponse[]>> {
    const response = await get<TenantHubApiResponse<KeycloakRoleResponse[]>>(
      roleEndpoints.getKeycloakRoles(realmName, clientId),
    );
    return response.data;
  }

  /**
   * Import CLIENT roles from Keycloak into the database.
   * POST /Role/sync-from-keycloak
   */
  async syncFromKeycloak(realmName: string, clientId: string, appId?: ID): Promise<TenantHubApiResponse<boolean>> {
    const response = await post<TenantHubApiResponse<boolean>>(
      roleEndpoints.syncFromKeycloak(realmName, clientId, appId),
      {},
    );
    return response.data;
  }

  /**
   * Check whether a role code exists in the database and/or Keycloak.
   * GET /Role/validate/:roleCode
   */
  async validate(
    roleCode: string,
    realmName: string,
    clientId?: string,
  ): Promise<TenantHubApiResponse<RoleExistenceValidation>> {
    const response = await get<TenantHubApiResponse<RoleExistenceValidation>>(
      roleEndpoints.validate(roleCode, realmName, clientId),
    );
    return response.data;
  }

  // ── Scope assignments (require tenant context) ──────────────────────────

  /**
   * Get the entities / business units / financial years assigned to a role.
   * GET /Role/:roleId/scope-assignments
   */
  async getScopeAssignments(roleId: ID): Promise<TenantHubApiResponse<RoleScopeAssignmentResponse>> {
    const response = await get<TenantHubApiResponse<RoleScopeAssignmentResponse>>(
      roleEndpoints.scopeAssignments(roleId),
    );
    return response.data;
  }

  /**
   * Get only the defaults summary for a role.
   * GET /Role/:roleId/scope-defaults
   */
  async getScopeDefaults(roleId: ID): Promise<TenantHubApiResponse<ScopeDefaultsSummary>> {
    const response = await get<TenantHubApiResponse<ScopeDefaultsSummary>>(
      roleEndpoints.scopeDefaults(roleId),
    );
    return response.data;
  }

  /**
   * Change which assigned entity / BU / FY is the default for a role.
   * PUT /Role/:roleId/scope-defaults
   */
  async updateScopeDefault(
    roleId: ID,
    data: UpdateRoleScopeDefaultRequest,
  ): Promise<TenantHubApiResponse<RoleScopeAssignmentResponse>> {
    const response = await put<TenantHubApiResponse<RoleScopeAssignmentResponse>>(
      roleEndpoints.updateScopeDefault(roleId),
      data,
    );
    return response.data;
  }

  /**
   * Remove every scope assignment from a role.
   * DELETE /Role/:roleId/scope-assignments
   */
  async removeScopeAssignments(roleId: ID): Promise<TenantHubApiResponse<boolean>> {
    const response = await del<TenantHubApiResponse<boolean>>(
      roleEndpoints.removeScopeAssignments(roleId),
    );
    return response.data;
  }
}

/**
 * Export singleton instance
 */
export const roleService = new RoleService();
export default roleService;
